import React, { useState } from 'react';
import { Alert, Button, Card, Select, Space, Table, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { getMonitorApiBaseUrl } from '../../config/env';
import { monitorRequest } from './api';
import { AlertHistoryEntry, AlertPage } from './alertTypes';
import { formatDateTime } from '../../utils/format';
import { DEFAULT_REFRESH_INTERVAL_MS, REFRESH_INTERVAL_OPTIONS } from './queries';

const PAGE_SIZE = 10;

export const AlertHistory: React.FC = () => {
  const baseURL = getMonitorApiBaseUrl();
  const [page, setPage] = useState(1);
  const [refreshInterval, setRefreshInterval] = useState(DEFAULT_REFRESH_INTERVAL_MS);
  const history = useQuery({
    queryKey: ['monitor-alerts', baseURL, 'history', page],
    enabled: !!baseURL,
    retry: false,
    refetchInterval: refreshInterval || false,
    queryFn: ({ signal }) => monitorRequest<AlertPage<AlertHistoryEntry>>('GET', '/api/v1/alerts/history', signal,
      { limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE }),
  });

  if (!baseURL) return <Alert type="info" showIcon message="Monitor API is not configured for this environment" />;
  return <Space direction="vertical" size="middle" className="w-full">
    <Card title="Alert History" extra={<Space wrap>
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>Auto refresh
        <Select aria-label="Alert history refresh interval" style={{ width: 100 }}
          value={refreshInterval} onChange={setRefreshInterval} options={REFRESH_INTERVAL_OPTIONS} />
      </label>
      <Button loading={history.isFetching} onClick={() => history.refetch()}>Refresh</Button>
    </Space>} styles={{ header: { flexWrap: 'wrap', gap: 12, paddingBlock: 12 } }}>
      {history.isError && <Alert type="error" showIcon
        message={history.error instanceof Error ? history.error.message : 'Unable to load alert history'}
        action={<Button onClick={() => history.refetch()}>Retry</Button>} />}
      <Table<AlertHistoryEntry> rowKey="id" loading={history.isFetching} dataSource={history.data?.items || []}
        scroll={{ x: 1000 }} locale={{ emptyText: 'No alert history' }}
        pagination={{ current: page, pageSize: PAGE_SIZE, total: history.data?.total || 0, showSizeChanger: false, onChange: setPage }}
        expandable={{
          expandedRowRender: (entry) => <pre style={{ margin: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {JSON.stringify(entry.detail, null, 2)}
          </pre>
        }}
        columns={[
          { title: 'Time', dataIndex: 'time', render: formatDateTime },
          { title: 'Event', dataIndex: 'kind', render: (value: string) => <Tag>{value === 'firing' ? 'Fire' : 'Test'}</Tag> },
          { title: 'Rule', render: (_, entry) => typeof entry.detail.rule_name === 'string' ? entry.detail.rule_name : entry.rule_id || '—' },
          {
            title: 'Delivery status', render: (_, entry) => {
              const status = entry.detail.delivery_status;
              const labels: Record<string, string> = { sent: 'Delivered', partial: 'Partially delivered', pending: 'Pending', failed: 'Failed', cancelled: 'Cancelled', not_queued: 'Not queued' };
              return <Tag color={status === 'sent' ? 'green' : status === 'failed' ? 'red' : 'default'}>{typeof status === 'string' ? labels[status] || status : 'Unknown'}</Tag>;
            }
          },
          {
            title: 'Channel deliveries', render: (_, entry) => Array.isArray(entry.detail.deliveries)
              ? entry.detail.deliveries.map((delivery: { channel_id: string; status: string; message?: string }) =>
                <div key={delivery.channel_id}>{delivery.channel_id}: {delivery.status}{delivery.message ? ` — ${delivery.message}` : ''}</div>) : '—'
          },
        ]} />
    </Card>
  </Space>;
};
